import { useEffect, useMemo, useState } from "react";
import { styled } from "@glitz/react";
import {
  spacing,
  green,
  darkGrey,
  fontWeight,
  fontSize,
  grey,
  black,
  red,
} from "../theme";
import {
  isShippingClassLight,
  useShipping,
  type DeliverySelection,
} from "../context/ShippingContext";
import { useCart } from "../context/CartContext";
import {
  fetchDeliveryOptions,
  fetchServicePoints,
  type TmaDeliveryOption,
  type TmaExpressAddon,
  type TmaServicePoint,
} from "../api/tmaShipping.api";

const normalizeZip = (value: string) => value.replace(/\s+/g, "").trim();
const isValidSwedishZip = (value: string) => /^\d{5}$/.test(normalizeZip(value));

type Props = {
  postalCode: string;
  city?: string;
};

function buildSelection(
  opt: TmaDeliveryOption,
  expressSelected: boolean,
  expressAddon: TmaExpressAddon | null,
  previous?: DeliverySelection | null
): DeliverySelection {
  const allow = opt.allowsExpress !== false && opt.type !== "store_pickup";
  const useExpress = Boolean(expressSelected && allow && expressAddon);
  const surchargeOre = useExpress ? expressAddon!.priceOre : 0;
  const surcharge = useExpress ? expressAddon!.price : 0;
  const extras = [
    ...(opt.additionalServiceCodes || []),
    ...(useExpress ? expressAddon!.additionalServiceCodes || [] : []),
  ];
  const title =
    useExpress && expressAddon
      ? `${opt.title} + ${expressAddon.title}`
      : opt.title;

  return {
    optionId: opt.id,
    serviceCode: opt.serviceCode,
    basicServiceCode: opt.basicServiceCode,
    type: opt.type,
    title,
    basePriceOre: opt.priceOre,
    basePrice: opt.price,
    priceOre: opt.priceOre + surchargeOre,
    price: opt.price + surcharge,
    expressSelected: useExpress,
    expressSurchargeOre: surchargeOre,
    requiresServicePoint: opt.requiresServicePoint,
    servicePointType: opt.servicePointType,
    additionalServiceCodes: extras,
    servicePointId: previous?.optionId === opt.id ? previous.servicePointId : undefined,
    servicePointName:
      previous?.optionId === opt.id ? previous.servicePointName : undefined,
    servicePointStreet:
      previous?.optionId === opt.id ? previous.servicePointStreet : undefined,
    servicePointCity:
      previous?.optionId === opt.id ? previous.servicePointCity : undefined,
    servicePointPostalCode:
      previous?.optionId === opt.id ? previous.servicePointPostalCode : undefined,
  };
}

/**
 * Visar TMA-fraktalternativ + ombud/box för ett givet postnummer.
 * Postnummer/kontakt/adress hanteras av TmaCheckoutShippingStep.
 */
const DeliveryOptionsWidget = ({ postalCode: rawPostal, city }: Props) => {
  const { deliverySelection, setDeliverySelection } = useShipping();
  const { cart, getMerchandiseAfterPackage } = useCart();
  const postalCode = normalizeZip(rawPostal);
  const cartTotalSek = getMerchandiseAfterPackage();
  const cartIsLight =
    cart.length > 0 &&
    cart.every((item) => isShippingClassLight(item.shipping_class));
  const allItemsInStock =
    cart.length > 0 &&
    cart.every(
      (item) => item.in_stock === true || item.stock_status === "instock"
    );

  const [options, setOptions] = useState<TmaDeliveryOption[]>([]);
  const [expressAddon, setExpressAddon] = useState<TmaExpressAddon | null>(
    null
  );
  const [freeShippingReached, setFreeShippingReached] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(999);
  const [points, setPoints] = useState<TmaServicePoint[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingPoints, setLoadingPoints] = useState(false);
  const [error, setError] = useState("");

  const visibleOptions = useMemo(() => {
    return options.filter((opt) => {
      const wc = opt.weightClass || "any";
      if (wc === "any") return true;
      if (cartIsLight) return wc === "light";
      return wc === "normal";
    });
  }, [options, cartIsLight]);

  const selectedOptionId = deliverySelection?.optionId;
  const selectedOption = visibleOptions.find((o) => o.id === selectedOptionId);
  const needsPoints = Boolean(selectedOption?.requiresServicePoint);
  const pointType = selectedOption?.servicePointType ?? null;
  const showExpress =
    Boolean(expressAddon) &&
    Boolean(selectedOption) &&
    selectedOption?.allowsExpress !== false &&
    selectedOption?.type !== "store_pickup";
  const expressDisabled = showExpress && !allItemsInStock;

  useEffect(() => {
    setDeliverySelection(null);
    setPoints([]);
    setOptions([]);
    setExpressAddon(null);

    if (!isValidSwedishZip(postalCode)) return;

    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      setError("");
      try {
        const result = await fetchDeliveryOptions({
          postalCode,
          cartTotalSek,
        });
        if (!cancelled) {
          setOptions(result.options);
          setExpressAddon(result.expressAddon || null);
          setFreeShippingReached(Boolean(result.freeShippingReached));
          if (result.freeShippingThreshold) {
            setFreeShippingThreshold(result.freeShippingThreshold);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setOptions([]);
          setExpressAddon(null);
          setError(e instanceof Error ? e.message : "Kunde inte hämta frakt");
        }
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [postalCode, cartTotalSek, setDeliverySelection]);

  useEffect(() => {
    if (!needsPoints || !isValidSwedishZip(postalCode) || !selectedOptionId) {
      setPoints([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingPoints(true);
      setError("");
      try {
        const type =
          pointType === "locker"
            ? "locker"
            : pointType === "service_point"
              ? "service_point"
              : "all";
        const list = await fetchServicePoints({
          postalCode,
          type,
          city,
        });
        if (!cancelled) setPoints(list);
      } catch (e) {
        if (!cancelled) {
          setPoints([]);
          setError(
            e instanceof Error ? e.message : "Kunde inte hämta utlämningsställen"
          );
        }
      } finally {
        if (!cancelled) setLoadingPoints(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [needsPoints, selectedOptionId, pointType, postalCode, city]);

  // Express kräver lager — stäng av om något saknas
  useEffect(() => {
    if (!deliverySelection?.expressSelected || allItemsInStock) return;
    const opt = visibleOptions.find((o) => o.id === deliverySelection.optionId);
    if (!opt) return;
    setDeliverySelection(
      buildSelection(opt, false, expressAddon, deliverySelection)
    );
  }, [
    allItemsInStock,
    deliverySelection,
    expressAddon,
    setDeliverySelection,
    visibleOptions,
  ]);

  const selectOption = (opt: TmaDeliveryOption) => {
    const keepExpress =
      Boolean(deliverySelection?.expressSelected) &&
      opt.allowsExpress !== false &&
      opt.type !== "store_pickup" &&
      allItemsInStock;
    setDeliverySelection(
      buildSelection(opt, keepExpress, expressAddon, deliverySelection)
    );
  };

  const toggleExpress = (checked: boolean) => {
    if (!selectedOption || expressDisabled) return;
    setDeliverySelection(
      buildSelection(selectedOption, checked, expressAddon, deliverySelection)
    );
  };

  const selectPoint = (point: TmaServicePoint) => {
    if (!deliverySelection) return;
    setDeliverySelection({
      ...deliverySelection,
      servicePointId: point.id,
      servicePointName: point.name,
      servicePointStreet: point.street,
      servicePointCity: point.city,
      servicePointPostalCode: point.postalCode,
    });
  };

  if (!isValidSwedishZip(postalCode)) return null;

  return (
    <Wrapper>
      {loadingOptions && <Hint>Hämtar alternativ…</Hint>}
      {error ? <ErrorText>{error}</ErrorText> : null}
      {freeShippingReached ? (
        <Hint>
          Fri frakt-nivå nådd (≥ {freeShippingThreshold} kr) — vissa metoder
          kostar mindre.
        </Hint>
      ) : null}

      <OptionsWrapper>
        {visibleOptions.map((opt) => {
          const isSelected = deliverySelection?.optionId === opt.id;
          const showOld =
            Boolean(opt.freeShippingApplied) &&
            opt.listPrice != null &&
            opt.listPrice > opt.price;
          return (
            <Option
              key={opt.id}
              onClick={() => selectOption(opt)}
              style={isSelected ? { borderColor: green } : undefined}
            >
              <RadioOuter>{isSelected ? <RadioInner /> : null}</RadioOuter>
              <OptionContent>
                <div>
                  <OptionLabel>{opt.title}</OptionLabel>
                  {opt.description ? (
                    <OptionDesc>{opt.description}</OptionDesc>
                  ) : null}
                </div>
                <PriceWrap>
                  {showOld ? <OldPrice>{opt.listPrice} kr</OldPrice> : null}
                  <OptionPrice>
                    {opt.price === 0 ? "0 kr" : `${opt.price} kr`}
                  </OptionPrice>
                </PriceWrap>
              </OptionContent>
            </Option>
          );
        })}
      </OptionsWrapper>

      {showExpress && expressAddon ? (
        <ExpressRow
          onClick={() => !expressDisabled && toggleExpress(!deliverySelection?.expressSelected)}
          style={
            expressDisabled
              ? { opacity: 0.65, cursor: "not-allowed" }
              : deliverySelection?.expressSelected
                ? { borderColor: green }
                : undefined
          }
        >
          <ExpressCheck
            type="checkbox"
            checked={Boolean(deliverySelection?.expressSelected)}
            disabled={expressDisabled}
            onChange={(e) => toggleExpress(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
          <OptionContent>
            <div>
              <OptionLabel>{expressAddon.title}</OptionLabel>
              <OptionDesc>
                {expressDisabled
                  ? "Kräver att alla varor finns i lager"
                  : expressAddon.description || "Lägg till prioriterad hantering"}
              </OptionDesc>
            </div>
            <OptionPrice>+{expressAddon.price} kr</OptionPrice>
          </OptionContent>
        </ExpressRow>
      ) : null}

      {selectedOption?.requiresServicePoint ? (
        <PointsSection>
          <PointsHeader>
            {selectedOption.servicePointType === "locker"
              ? "Välj paketbox"
              : "Välj utlämningsställe"}
          </PointsHeader>
          {loadingPoints && <Hint>Hämtar ställen…</Hint>}
          {!loadingPoints && points.length === 0 ? (
            <Hint>Inga ställen hittades för postnumret.</Hint>
          ) : null}
          <PointsList>
            {points.map((p) => {
              const active = deliverySelection?.servicePointId === p.id;
              return (
                <PointRow
                  key={p.id}
                  onClick={() => selectPoint(p)}
                  style={active ? { borderColor: green } : undefined}
                >
                  <RadioOuter>{active ? <RadioInner /> : null}</RadioOuter>
                  <div>
                    <OptionLabel>{p.name}</OptionLabel>
                    <OptionDesc>
                      {[p.street, p.postalCode, p.city].filter(Boolean).join(", ")}
                      {typeof p.distanceMeters === "number"
                        ? ` · ${p.distanceMeters} m`
                        : ""}
                    </OptionDesc>
                  </div>
                </PointRow>
              );
            })}
          </PointsList>
        </PointsSection>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: spacing.m,
});

const Hint = styled.div({
  textAlign: "center",
  fontSize: fontSize.s,
  color: darkGrey,
});

const ErrorText = styled.div({
  textAlign: "center",
  fontSize: fontSize.s,
  color: red,
  fontWeight: fontWeight.bold,
});

const OptionsWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

const Option = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.m,
  padding: { xy: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "2px" } },
  borderRadius: "8px",
  cursor: "pointer",
  transition: {
    property: "border-color",
    duration: "0.15s",
    timingFunction: "ease",
  },
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "2px" } },
  },
});

const ExpressRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.m,
  padding: { xy: spacing.m },
  border: { xy: { style: "solid", color: grey, width: "2px" } },
  borderRadius: "8px",
  cursor: "pointer",
  backgroundColor: "#fafafa",
});

const ExpressCheck = styled.input({
  width: "18px",
  height: "18px",
  flexShrink: 0,
  cursor: "pointer",
});

const RadioOuter = styled.div({
  width: "20px",
  height: "20px",
  borderRadius: "50%",
  border: { xy: { style: "solid", color: darkGrey, width: "2px" } },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const RadioInner = styled.div({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor: green,
});

const OptionContent = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  gap: spacing.m,
});

const OptionLabel = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
});

const OptionDesc = styled.div({
  fontSize: fontSize.s,
  color: darkGrey,
  margin: { top: spacing.xxs },
});

const PriceWrap = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  flexShrink: 0,
});

const OldPrice = styled.span({
  fontSize: fontSize.s,
  color: darkGrey,
  textDecoration: "line-through",
});

const OptionPrice = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  color: black,
  flexShrink: 0,
});

const PointsSection = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  marginTop: spacing.s,
});

const PointsHeader = styled.h3({
  fontSize: fontSize.m,
  margin: { xy: 0 },
  textAlign: "center",
});

const PointsList = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  maxHeight: "320px",
  overflowY: "auto",
});

const PointRow = styled.div({
  display: "flex",
  alignItems: "flex-start",
  gap: spacing.m,
  padding: { xy: spacing.s },
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  borderRadius: "8px",
  cursor: "pointer",
  ":hover": {
    border: { xy: { style: "solid", color: green, width: "1px" } },
  },
});

export default DeliveryOptionsWidget;
